import { FiDownload as _FiDownload } from "@react-icons/all-files/fi/FiDownload.js";
import { FiSettings as _FiSettings } from "@react-icons/all-files/fi/FiSettings.js";
import { FiSearch as _FiSearch } from "@react-icons/all-files/fi/FiSearch.js";
import { FiClock as _FiClock } from "@react-icons/all-files/fi/FiClock.js";
import _isNumber from "lodash/isNumber";
import _isEqual from "lodash/isEqual";
import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { observer, MobXProviderContext } from 'mobx-react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { themeGet } from 'styled-system';
import { Box, Button, Flex, Icon, Link, Menu, MenuItem, Popover, Spinner, Switch, Tag, Text, EmptyState } from 'core/components';
import { getStartAndEndFromLookback, timezone } from 'core/util/dateUtils';
import QueryModel from 'app/stores/ude/QueryModel';
import { getMsValue, healthToIntent, healthToIcon } from 'app/views/synthetics/utils/syntheticsUtils';
import { formatHealth } from 'app/views/ude/dataviews/util/unitFormatter';
import { arrayToCsv } from 'app/util/arrayToCsv';
import LabelList from 'app/components/labels/LabelList';

// --- Typed wrappers for JS imports ---

/** Typed wrapper for healthToIntent (untyped JS util) */
const getHealthIntent = healthInput => healthToIntent(healthInput);

/** Typed wrapper for healthToIcon (untyped JS util) */
const getHealthIcon = healthInput => healthToIcon(healthInput);

/** MobX model label — supports both plain objects and MobX models with get() */

/** Typed LabelList — untyped JS component */
const TypedLabelList = LabelList;

// --- Constants & Types ---

const TEST_TYPE_TO_MEASUREMENT = {
  agent: '/synthetics/ping',
  isFlowBased: '/synthetics/ping',
  isHostname: '/synthetics/ping',
  isBulkIpTest: '/synthetics/ping',
  isMesh: '/synthetics/ping',
  isDns: '/synthetics/dns',
  url: '/synthetics/http',
  'page-load': '/synthetics/pageload'
};
const MEASUREMENT_METRICS = {
  '/synthetics/ping': {
    measurement: '/synthetics/ping',
    metrics: [{
      name: 'ping_latency_avg',
      label: 'Latency',
      unit: 'duration:microseconds',
      healthMetric: 'ping_latency_health'
    }, {
      name: 'ping_jitter_avg',
      label: 'Jitter',
      unit: 'duration:microseconds',
      healthMetric: 'ping_jitter_health'
    }, {
      name: 'ping_packet_loss_pct',
      label: 'Packet Loss',
      unit: 'percent',
      healthMetric: 'ping_packet_loss_health'
    }],
    healthMetrics: [{
      name: 'ping_latency_health'
    }, {
      name: 'ping_jitter_health'
    }, {
      name: 'ping_packet_loss_health'
    }]
  },
  '/synthetics/dns': {
    measurement: '/synthetics/dns',
    metrics: [{
      name: 'dns_latency_avg',
      label: 'DNS Latency',
      unit: 'duration:microseconds',
      healthMetric: 'dns_latency_health'
    }],
    healthMetrics: [{
      name: 'dns_latency_health'
    }]
  },
  '/synthetics/http': {
    measurement: '/synthetics/http',
    metrics: [{
      name: 'http_latency_avg',
      label: 'Latency',
      unit: 'duration:microseconds',
      healthMetric: 'http_latency_health'
    }],
    healthMetrics: [{
      name: 'http_latency_health'
    }]
  },
  '/synthetics/pageload': {
    measurement: '/synthetics/pageload',
    metrics: [{
      name: 'pageload_latency_avg',
      label: 'Latency',
      unit: 'duration:microseconds',
      healthMetric: 'pageload_latency_health'
    }],
    healthMetrics: [{
      name: 'pageload_latency_health'
    }]
  }
};
const QUERY_SETTINGS = {
  chart: {
    enable: false,
    type: 'table',
    colors: 'default',
    legend: {
      enable: false,
      position: 'right',
      mode: 'condensed',
      showRollups: true
    },
    xAxis: {
      timezone: 'utc'
    },
    yAxis: {
      type: 'linear'
    }
  }
};

// --- Styled Components ---

const StyledTable = styled.table.withConfig({
  displayName: "SynthTestListDashboardItem__StyledTable",
  componentId: "sc-1aqvqlg-0"
})(["&&{width:100%;border-collapse:collapse;font-size:13px;}"]);
const StyledTh = styled.th.withConfig({
  displayName: "SynthTestListDashboardItem__StyledTh",
  componentId: "sc-1aqvqlg-1"
})(["&&{text-align:", ";padding:8px 10px;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:", ";border-bottom:1px solid rgba(0,0,0,0.1);white-space:nowrap;}"], ({
  align
}) => align || 'left', themeGet('colors.muted'));
const StyledTd = styled.td.withConfig({
  displayName: "SynthTestListDashboardItem__StyledTd",
  componentId: "sc-1aqvqlg-2"
})(["&&{padding:8px 10px;border-bottom:1px solid rgba(0,0,0,0.1);vertical-align:middle;text-align:", ";}"], ({
  align
}) => align || 'left');
const StyledTr = styled.tr.withConfig({
  displayName: "SynthTestListDashboardItem__StyledTr",
  componentId: "sc-1aqvqlg-3"
})(["&&{&:hover{background:", ";}}"], themeGet('colors.primaryBackground'));
const SearchInput = styled.input.withConfig({
  displayName: "SynthTestListDashboardItem__SearchInput",
  componentId: "sc-1aqvqlg-4"
})(["&&{width:100%;padding:8px 12px 8px 36px;border:1px solid ", ";border-radius:4px;font-size:13px;outline:none;background:", ";color:", ";&::placeholder{color:", ";}&:focus{border-color:", ";}}"], themeGet('colors.border'), themeGet('colors.appBackground'), themeGet('colors.body'), themeGet('colors.muted'), themeGet('colors.primary'));
const SearchIcon = styled.div.withConfig({
  displayName: "SynthTestListDashboardItem__SearchIcon",
  componentId: "sc-1aqvqlg-5"
})(["&&{position:absolute;left:10px;top:50%;transform:translateY(-50%);pointer-events:none;}"]);
const HealthDot = styled.span.withConfig({
  displayName: "SynthTestListDashboardItem__HealthDot",
  componentId: "sc-1aqvqlg-6"
})(["&&{display:inline-block;width:8px;height:8px;border-radius:50%;background:", ";margin-right:6px;}"], ({
  color
}) => color);
const DataViewToggle = styled.button.withConfig({
  displayName: "SynthTestListDashboardItem__DataViewToggle",
  componentId: "sc-1aqvqlg-7"
})(["&&{padding:4px 12px;font-size:12px;font-weight:600;background:none;border:none;cursor:pointer;color:", ";border-bottom:2px solid transparent;", " &:hover{color:", ";}}"], themeGet('colors.body'), ({
  active
}) => active && css(["color:", ";border-bottom-color:", ";"], themeGet('colors.primary'), themeGet('colors.primary')), themeGet('colors.primary'));
const SummaryTab = styled.button.withConfig({
  displayName: "SynthTestListDashboardItem__SummaryTab",
  componentId: "sc-1aqvqlg-8"
})(["&&{padding:8px 20px;text-align:center;background:none;border:none;cursor:pointer;border-bottom:3px solid transparent;margin-bottom:-1px;", " &:hover{border-bottom-color:", ";}}"], ({
  active
}) => active && css(["border-bottom-color:", ";"], themeGet('colors.primary')), themeGet('colors.primaryLight', 'colors.primary'));

// --- Query Builder ---

function buildTestListQuery({
  measurement,
  measurementMetrics,
  labelIds,
  rollupFn,
  lookbackSeconds,
  startTime,
  endTime
}) {
  const metrics = [...measurementMetrics.metrics.map(m => ({
    name: m.name
  })), ...measurementMetrics.healthMetrics.map(m => ({
    name: m.name
  }))];
  const rollups = [...measurementMetrics.metrics.map(m => ({
    metric: m.name,
    fn: rollupFn
  })), ...measurementMetrics.healthMetrics.map(m => ({
    metric: m.name,
    fn: 'min'
  }))];
  const filters = {};
  if (labelIds && labelIds.length > 0) {
    const dimensionFilters = {
      connector: 'All',
      filterGroups: [{
        connector: 'Any',
        not: false,
        filters: labelIds.map(id => ({
          filterField: 'ktlabel_syn_test',
          operator: '=',
          filterValue: String(id)
        }))
      }]
    };
    filters.dimensions = dimensionFilters;
  }
  return {
    measurement,
    dimensions: [{
      name: 'test_id'
    }],
    metrics,
    filters,
    rollups,
    time: {
      lookback: lookbackSeconds,
      start: startTime,
      end: endTime
    },
    viz: {
      type: 'table',
      limit: 1000
    },
    limit: 1000
  };
}

// --- Time Range Helper ---

function getTimeRange(query) {
  const {
    lookback_seconds,
    starting_time,
    ending_time
  } = query;
  if (starting_time != null && ending_time != null) {
    return {
      lookbackSeconds: 0,
      startTime: _isNumber(starting_time) ? starting_time : moment.utc(starting_time).unix(),
      endTime: _isNumber(ending_time) ? ending_time : moment.utc(ending_time).unix()
    };
  }
  if (lookback_seconds === 1) {
    const start = timezone.momentFn().date(1).hour(0).minute(0).second(0).millisecond(0);
    const end = timezone.momentFn();
    return {
      lookbackSeconds: 0,
      startTime: moment.utc(start).unix(),
      endTime: moment.utc(end).unix()
    };
  }
  if (lookback_seconds === 2) {
    const start = timezone.momentFn().date(1).hour(0).minute(0).second(0).millisecond(0).subtract(1, 'month');
    const end = timezone.momentFn(start).endOf('month');
    return {
      lookbackSeconds: 0,
      startTime: moment.utc(start).unix(),
      endTime: moment.utc(end).unix()
    };
  }
  if (lookback_seconds > 2) {
    const {
      start,
      end
    } = getStartAndEndFromLookback(lookback_seconds, 60);
    return {
      lookbackSeconds: 0,
      startTime: _isNumber(start) ? start : moment.utc(start).unix(),
      endTime: _isNumber(end) ? end : moment.utc(end).unix()
    };
  }
  return {
    lookbackSeconds: 3600,
    startTime: 0,
    endTime: 0
  };
}

// --- Health Helpers ---

/** Map a UDE health code (100/200/300) to a lowercase label using formatHealth from unitFormatter */
function healthCodeToLabel(value) {
  return formatHealth(value).display.toLowerCase();
}
function deriveHealth(rollups, healthMetrics) {
  const healthValues = healthMetrics.map(m => rollups[`min_${m.name}`]).filter(v => v != null && v > 0);
  const worstHealth = healthValues.length > 0 ? Math.min(...healthValues) : null;
  const healthLabel = worstHealth !== null ? healthCodeToLabel(worstHealth) : 'pending';
  return {
    label: healthLabel,
    intent: getHealthIntent({
      health: healthLabel
    })
  };
}
function deriveMetricHealth(rollups, healthMetric) {
  if (!healthMetric) {
    return null;
  }
  const val = rollups[`min_${healthMetric}`];
  if (val == null || val <= 0) {
    return null;
  }
  return healthCodeToLabel(val);
}
function formatMetricValue(value, unit) {
  if (value == null || Number.isNaN(value)) {
    return '---';
  }
  if (unit === 'percent') {
    // UDE rollup values for percentage metrics are already in 0-100 range
    return `${value === 100 ? '100' : Number(value.toFixed(2))}%`;
  }
  return getMsValue(value);
}

// --- Component ---

/** Minimal interface for a MobX model with .get() accessor — partial type during JS→TS migration */

/** Partial type for the Collection.get() pattern used by $syn stores */

const SynthTestListDashboardItem = observer(({
  $syn,
  dataview
}) => {
  const {
    $app,
    $labels
  } = useContext(MobXProviderContext);
  const {
    synth_test_type,
    synth_test_id,
    lookback_seconds,
    starting_time,
    ending_time
  } = dataview.query;
  const [rollupFn, setRollupFn] = useState('avg');
  const [searchFilter, setSearchFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState(null);
  const measurement = MEASUREMENT_METRICS[synth_test_type] ? synth_test_type : TEST_TYPE_TO_MEASUREMENT[synth_test_type];
  const measurementMetrics = measurement ? MEASUREMENT_METRICS[measurement] : null;

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {
      labels: true,
      healthIcons: true
    };
    if (measurementMetrics) {
      measurementMetrics.metrics.forEach(m => {
        initial[m.name] = true;
      });
    }
    return initial;
  });

  // Re-seed column visibility when measurement changes (e.g. new metric columns)
  useEffect(() => {
    if (!measurementMetrics) {
      return;
    }
    setColumnVisibility(prev => {
      const updated = {
        ...prev
      };
      measurementMetrics.metrics.forEach(m => {
        if (!(m.name in updated)) {
          updated[m.name] = true;
        }
      });
      return updated;
    });
  }, [measurement, measurementMetrics]);
  const toggleColumn = useCallback(column => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  }, []);

  // Build the UDE query
  const timeRange = useMemo(() => getTimeRange({
    lookback_seconds,
    starting_time,
    ending_time
  }), [lookback_seconds, starting_time, ending_time]);
  const query = useMemo(() => {
    if (!measurement || !measurementMetrics) {
      return null;
    }
    const labelIds = Array.isArray(synth_test_id) ? synth_test_id.map(String) : undefined;
    return buildTestListQuery({
      measurement,
      measurementMetrics,
      labelIds,
      rollupFn,
      lookbackSeconds: timeRange.lookbackSeconds,
      startTime: timeRange.startTime,
      endTime: timeRange.endTime
    });
  }, [measurement, measurementMetrics, synth_test_id, rollupFn, timeRange]);

  // Create QueryModel once
  const [queryModel] = useState(() => {
    const initialQuery = query || buildTestListQuery({
      measurement: '/synthetics/ping',
      measurementMetrics: MEASUREMENT_METRICS['/synthetics/ping'],
      rollupFn: 'avg',
      lookbackSeconds: 3600,
      startTime: 0,
      endTime: 0
    });
    return new QueryModel(initialQuery, QUERY_SETTINGS);
  });

  // Run query when it changes
  const prevQueryRef = useRef(null);
  useEffect(() => {
    if (!query) {
      return;
    }

    // Only run if query actually changed
    if (prevQueryRef.current && _isEqual(prevQueryRef.current, query)) {
      return;
    }
    prevQueryRef.current = query;
    queryModel.query = query;
    queryModel.run();
  }, [query, queryModel]);

  // Handle manual refresh via lastRefresh
  const lastRefreshRef = useRef(dataview.lastRefresh);
  useEffect(() => {
    if (dataview.lastRefresh !== lastRefreshRef.current) {
      lastRefreshRef.current = dataview.lastRefresh;
      if (queryModel.hasRun) {
        queryModel.run();
      }
    }
  }, [dataview.lastRefresh, queryModel]);

  // Auto-refresh interval
  useEffect(() => {
    if (!dataview.updateFrequency || !query) {
      return undefined;
    }
    const interval = setInterval(() => queryModel.run(), dataview.updateFrequency * 1000);
    return () => clearInterval(interval);
  }, [dataview.updateFrequency, query, queryModel]);

  // Cleanup on unmount
  useEffect(() => () => queryModel.cleanup(), [queryModel]);

  // Signal fully loaded for dashboard export
  useEffect(() => {
    if (!measurement || !measurementMetrics || queryModel.fullyLoaded || queryModel.hasError) {
      dataview.setFullyLoaded();
    }
  }, [dataview, measurement, measurementMetrics, queryModel.fullyLoaded, queryModel.hasError]);

  // Derived data — must be above early returns to preserve hook order
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = queryModel.mainQueryRequest.results?.rows ?? [];
  const isExport = $app?.isExport;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleMetrics = measurementMetrics ? measurementMetrics.metrics.filter(m => columnVisibility[m.name] !== false) : [];
  const rollupLabel = rollupFn.charAt(0).toUpperCase() + rollupFn.slice(1);

  // Enrich rows with test model data for filtering and rendering
  const enrichedRows = useMemo(() => {
    if (!measurementMetrics) {
      return [];
    }
    return rows.map(row => {
      const testId = row.dimensions.test_id;
      const testModel = $syn.tests.get(testId) || $syn.communityPerformanceTests?.get(testId);
      const testName = testModel?.get('display_name') ?? `Test ${testId}`;
      const testLabels = testModel?.labels ?? [];
      const health = deriveHealth(row.rollups, measurementMetrics.healthMetrics);
      return {
        row,
        testId,
        testModel,
        testName,
        testLabels,
        health
      };
    });
  }, [rows, $syn.tests, $syn.communityPerformanceTests, measurementMetrics]);

  // Filter by search term and health
  const filteredRows = useMemo(() => {
    let filtered = enrichedRows;

    // Health filter
    if (healthFilter) {
      filtered = filtered.filter(({
        health
      }) => health.label === healthFilter);
    }

    // Search filter
    if (searchFilter.trim()) {
      const term = searchFilter.toLowerCase();
      filtered = filtered.filter(({
        testName,
        testLabels
      }) => {
        if (testName.toLowerCase().includes(term)) {
          return true;
        }
        if (!testLabels || !Array.isArray(testLabels)) {
          return false;
        }
        return testLabels.some(label => {
          const name = label?.get?.('name') ?? label?.name ?? '';
          return name.toLowerCase().includes(term);
        });
      });
    }
    return filtered;
  }, [enrichedRows, searchFilter, healthFilter]);

  // Health summary counts
  const healthCounts = useMemo(() => {
    const counts = {
      total: enrichedRows.length,
      healthy: 0,
      critical: 0,
      warning: 0
    };
    enrichedRows.forEach(({
      health
    }) => {
      if (health.label === 'healthy') {
        counts.healthy += 1;
      } else if (health.label === 'critical') {
        counts.critical += 1;
      } else if (health.label === 'warning') {
        counts.warning += 1;
      }
    });
    return counts;
  }, [enrichedRows]);

  // Resolve selected labels for header display
  const selectedLabels = useMemo(() => {
    if (!Array.isArray(synth_test_id) || !$labels) {
      return [];
    }
    return synth_test_id.map(id => $labels.labels.get(String(id))).filter(Boolean);
  }, [synth_test_id, $labels]);

  // Format time range for display
  const timeRangeDisplay = useMemo(() => {
    if (!timeRange.startTime || !timeRange.endTime) {
      return '';
    }
    const fmt = 'YYYY-MM-DD HH:mm';
    const start = moment.unix(timeRange.startTime).format(fmt);
    const end = moment.unix(timeRange.endTime).format(fmt);
    return `${start} - ${end}`;
  }, [timeRange]);

  // CSV export
  const exportCsv = useCallback(() => {
    const columns = ['Health', 'Test Name'];
    if (columnVisibility.labels !== false) {
      columns.push('Labels');
    }
    visibleMetrics.forEach(m => {
      columns.push(`${rollupLabel} ${m.label}`);
      if (m.healthMetric) {
        columns.push(`${m.label} Health`);
      }
    });
    const healthValueToLabel = val => {
      if (val == null || val <= 0) {
        return 'pending';
      }
      return healthCodeToLabel(val);
    };
    const lines = filteredRows.map(({
      testName,
      testLabels,
      health,
      row
    }) => {
      const cells = [health.label, testName];
      if (columnVisibility.labels !== false) {
        const labelNames = (testLabels ?? []).map(l => l?.get?.('name') ?? l?.name ?? '').join('; ');
        cells.push(labelNames);
      }
      visibleMetrics.forEach(m => {
        const rollupKey = `${rollupFn}_${m.name}`;
        cells.push(formatMetricValue(row.rollups[rollupKey], m.unit));
        if (m.healthMetric) {
          cells.push(healthValueToLabel(row.rollups[`min_${m.healthMetric}`]));
        }
      });
      return cells;
    });
    const csv = arrayToCsv(lines, columns);
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    const filterSuffix = healthFilter ? `-${healthFilter}` : '';
    a.download = `test-list-${rollupFn}${filterSuffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [filteredRows, visibleMetrics, columnVisibility, rollupFn, rollupLabel, healthFilter]);

  // --- Render ---

  if (!measurement || !measurementMetrics) {
    return /*#__PURE__*/React.createElement(EmptyState, {
      icon: "disable",
      title: "Unsupported test type",
      description: `Test type "${synth_test_type}" is not supported for the Test List display.`
    });
  }
  if (queryModel.isLoading) {
    return /*#__PURE__*/React.createElement(Flex, {
      flex: 1,
      alignItems: "center",
      justifyContent: "center"
    }, /*#__PURE__*/React.createElement(Spinner, {
      intent: "primary"
    }));
  }
  if (queryModel.hasError) {
    return /*#__PURE__*/React.createElement(EmptyState, {
      icon: "error",
      title: "Query Error",
      description: "Failed to load test data. Please try again."
    });
  }
  if (!queryModel.hasResults) {
    return /*#__PURE__*/React.createElement(EmptyState, {
      icon: "disable",
      title: "No results",
      description: "No test data found for the selected configuration and time range."
    });
  }
  return /*#__PURE__*/React.createElement(Flex, {
    flex: 1,
    flexDirection: "column",
    overflow: "hidden"
  }, (timeRangeDisplay || selectedLabels.length > 0) && /*#__PURE__*/React.createElement(Flex, {
    px: 2,
    pt: 2,
    pb: 1,
    alignItems: "center",
    gap: 2,
    flexWrap: "wrap"
  }, timeRangeDisplay && /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: 1
  }, /*#__PURE__*/React.createElement(_FiClock, {
    size: 14,
    color: "#9e9e9e"
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: 12,
    color: "muted"
  }, timeRangeDisplay)), selectedLabels.length > 0 && /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap"
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 11,
    fontWeight: 700,
    color: "muted",
    style: {
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  }, "Label:"), selectedLabels.map(label => /*#__PURE__*/React.createElement(Tag, {
    key: label.id,
    minimal: true,
    round: true,
    style: {
      borderLeft: `3px solid ${label?.get?.('color') || '#999'}`
    }
  }, label?.get?.('name') ?? label?.name ?? '')))), !isExport && /*#__PURE__*/React.createElement(Box, {
    px: 2,
    pt: 1,
    pb: 1,
    position: "relative"
  }, /*#__PURE__*/React.createElement(Box, {
    position: "relative"
  }, /*#__PURE__*/React.createElement(SearchIcon, null, /*#__PURE__*/React.createElement(_FiSearch, {
    size: 14,
    color: "#9e9e9e"
  })), /*#__PURE__*/React.createElement(SearchInput, {
    placeholder: "Filter tests by name or labels (e.g. 'P1', 'US-East')...",
    value: searchFilter,
    onChange: e => setSearchFilter(e.target.value)
  }))), /*#__PURE__*/React.createElement(Flex, {
    px: 2,
    py: 2,
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "thin"
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: 0
  }, /*#__PURE__*/React.createElement(SummaryTab, {
    active: healthFilter === null,
    onClick: () => setHealthFilter(null)
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 11,
    fontWeight: 700,
    color: "muted",
    as: "div",
    style: {
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  }, "Total"), /*#__PURE__*/React.createElement(Text, {
    fontSize: 22,
    fontWeight: 700,
    as: "div"
  }, healthCounts.total)), /*#__PURE__*/React.createElement(SummaryTab, {
    active: healthFilter === 'healthy',
    onClick: () => setHealthFilter(healthFilter === 'healthy' ? null : 'healthy')
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "center",
    mb: "2px"
  }, /*#__PURE__*/React.createElement(HealthDot, {
    color: "#43a047"
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: 11,
    fontWeight: 700,
    color: "muted",
    as: "span",
    style: {
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  }, "Healthy")), /*#__PURE__*/React.createElement(Text, {
    fontSize: 22,
    fontWeight: 700,
    as: "div",
    color: healthCounts.healthy > 0 ? 'body' : 'muted'
  }, healthCounts.healthy)), /*#__PURE__*/React.createElement(SummaryTab, {
    active: healthFilter === 'critical',
    onClick: () => setHealthFilter(healthFilter === 'critical' ? null : 'critical')
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "center",
    mb: "2px"
  }, /*#__PURE__*/React.createElement(HealthDot, {
    color: "#e53935"
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: 11,
    fontWeight: 700,
    color: "muted",
    as: "span",
    style: {
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  }, "Critical")), /*#__PURE__*/React.createElement(Text, {
    fontSize: 22,
    fontWeight: 700,
    as: "div",
    color: healthCounts.critical > 0 ? 'danger' : 'muted'
  }, healthCounts.critical)), /*#__PURE__*/React.createElement(SummaryTab, {
    active: healthFilter === 'warning',
    onClick: () => setHealthFilter(healthFilter === 'warning' ? null : 'warning')
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "center",
    mb: "2px"
  }, /*#__PURE__*/React.createElement(HealthDot, {
    color: "#fb8c00"
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: 11,
    fontWeight: 700,
    color: "muted",
    as: "span",
    style: {
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  }, "Warning")), /*#__PURE__*/React.createElement(Text, {
    fontSize: 22,
    fontWeight: 700,
    as: "div",
    color: healthCounts.warning > 0 ? 'warning' : 'muted'
  }, healthCounts.warning))), !isExport && /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: 2
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 11,
    fontWeight: 700,
    color: "muted",
    mr: 1,
    style: {
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  }, "Data View"), /*#__PURE__*/React.createElement(Flex, null, ['min', 'avg', 'max'].map(fn => /*#__PURE__*/React.createElement(DataViewToggle, {
    key: fn,
    active: rollupFn === fn,
    onClick: () => setRollupFn(fn)
  }, fn.toUpperCase()))), /*#__PURE__*/React.createElement(Popover, {
    position: "bottom-right",
    content: /*#__PURE__*/React.createElement(Menu, null, /*#__PURE__*/React.createElement(MenuItem, {
      shouldDismissPopover: false,
      text: "Labels",
      labelElement: /*#__PURE__*/React.createElement(Switch, {
        checked: columnVisibility.labels !== false,
        onChange: () => toggleColumn('labels')
      })
    }), /*#__PURE__*/React.createElement(MenuItem, {
      shouldDismissPopover: false,
      text: "Health Icons",
      labelElement: /*#__PURE__*/React.createElement(Switch, {
        checked: columnVisibility.healthIcons !== false,
        onChange: () => toggleColumn('healthIcons')
      })
    }), measurementMetrics.metrics.map(m => /*#__PURE__*/React.createElement(MenuItem, {
      key: m.name,
      shouldDismissPopover: false,
      text: m.label,
      labelElement: /*#__PURE__*/React.createElement(Switch, {
        checked: columnVisibility[m.name] !== false,
        onChange: () => toggleColumn(m.name)
      })
    })))
  }, /*#__PURE__*/React.createElement(Button, {
    icon: _FiSettings,
    small: true,
    minimal: true
  })), /*#__PURE__*/React.createElement(Button, {
    icon: _FiDownload,
    small: true,
    minimal: true,
    onClick: e => {
      e.stopPropagation();
      exportCsv();
    }
  }))), /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    overflow: "auto"
  }, /*#__PURE__*/React.createElement(StyledTable, null, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement(StyledTh, null, "Test Details"), visibleMetrics.map(m => /*#__PURE__*/React.createElement(StyledTh, {
    key: m.name,
    align: "right"
  }, rollupLabel, " ", m.label)))), /*#__PURE__*/React.createElement("tbody", null, filteredRows.map(({
    row,
    testId,
    testName,
    testLabels,
    health
  }) => /*#__PURE__*/React.createElement(StyledTr, {
    key: row.id
  }, /*#__PURE__*/React.createElement(StyledTd, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "flex-start",
    gap: 2
  }, /*#__PURE__*/React.createElement(Box, {
    pt: "2px"
  }, /*#__PURE__*/React.createElement(Tag, {
    minimal: true,
    intent: health.intent,
    fontWeight: "bold",
    style: {
      textTransform: 'capitalize'
    }
  }, health.label)), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Link, {
    to: `/v4/synthetics/tests/${testId}/results`,
    fontWeight: "bold"
  }, testName), columnVisibility.labels !== false && testLabels.length > 0 && /*#__PURE__*/React.createElement(Box, {
    mt: "2px"
  }, /*#__PURE__*/React.createElement(TypedLabelList, {
    labels: testLabels,
    maxLabelsToShow: 3
  }))))), visibleMetrics.map(m => {
    const rollupKey = `${rollupFn}_${m.name}`;
    const value = row.rollups[rollupKey];
    const metricHealth = deriveMetricHealth(row.rollups, m.healthMetric);
    const metricHealthIcon = metricHealth ? getHealthIcon({
      health: metricHealth
    }) : null;
    const metricHealthIntent = metricHealth ? getHealthIntent({
      health: metricHealth
    }) : null;
    const showHealthIcon = columnVisibility.healthIcons !== false;
    return /*#__PURE__*/React.createElement(StyledTd, {
      key: m.name,
      align: "right"
    }, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 1
    }, showHealthIcon && metricHealthIcon && /*#__PURE__*/React.createElement(Icon, {
      icon: metricHealthIcon,
      iconSize: 14,
      color: metricHealthIntent
    }), /*#__PURE__*/React.createElement(Text, {
      fontWeight: "bold",
      style: {
        fontVariantNumeric: 'tabular-nums',
        minWidth: '70px',
        textAlign: 'right',
        display: 'inline-block'
      }
    }, formatMetricValue(value, m.unit))));
  }))), filteredRows.length === 0 && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement(StyledTd, {
    colSpan: 1 + visibleMetrics.length,
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    muted: true,
    py: 3,
    as: "div"
  }, "No tests match your filter.")))))));
});
export default SynthTestListDashboardItem;

// Exported for testing
export { buildTestListQuery, deriveHealth, formatMetricValue, getTimeRange, TEST_TYPE_TO_MEASUREMENT, MEASUREMENT_METRICS };